import { useState, useEffect, useCallback } from 'react';
import StatusMessage from '../components/shared/StatusMessage';
import { authHeaders } from '../utils/auth';
import { apiFetch } from '../utils/api';
import { useBatch } from '../context/BatchContext';

const PAGE_SIZE = 50;

const fmtTime = (iso) => new Date(iso).toLocaleString();
const fmtUser = (u) => u ? `${u.fName} ${u.lName}` : 'system';
const NO_PHOTO_ACTIONS = new Set(['gallery_accessed', 'approval_email_sent', 'approval_email_failed']);
const fmtPhoto = (p, actionType) => {
    if (p) return p.originalFilename;
    if (NO_PHOTO_ACTIONS.has(actionType)) return '—';
    return '(deleted photo)';
};
const fmtBatch = (batchId, batches) => {
    if (!batchId) return '—';
    const batch = batches.find(b => b.id === batchId);
    return batch ? batch.production : '—';
};

export default function AuditLog() {
    const { batches, batchId, setBatchId } = useBatch();
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [state, setState] = useState({ status: 'loading', error: null });

    const offset = (page - 1) * PAGE_SIZE;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const firstEntry = total === 0 ? 0 : offset + 1;
    const lastEntry = Math.min(offset + PAGE_SIZE, total);

    const fetchLogs = useCallback(() => {
        setState({ status: 'loading', error: null });
        const params = new URLSearchParams({ limit: PAGE_SIZE, offset });
        if (batchId) params.set('batchId', batchId);

        apiFetch(`/api/audit?${params}`, { headers: authHeaders() })
            .then(r => r.json())
            .then(({ logs, total }) => {
                setLogs(logs || []);
                setTotal(total ?? 0);
                setState({ status: 'ready', error: null });
            })
            .catch(err => setState({ status: 'error', error: err.message }));
    }, [batchId, offset]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const handleBatchChange = (e) => {
        setPage(1);
        setBatchId(e.target.value);
    };

    return (
        <div className='audit-log'>
            <div className='audit-log-controls'>
                <label className='audit-filter-label'>
                    Filter by batch
                    <select
                        className='audit-filter-select'
                        value={batchId}
                        onChange={handleBatchChange}
                    >
                        <option value=''>All batches</option>
                        {batches.map(b => (
                            <option key={b.id} value={b.id}>{b.production}</option>
                        ))}
                    </select>
                </label>
            </div>

            {state.status !== 'ready' && (
                <StatusMessage state={state} onRetry={fetchLogs} />
            )}

            {state.status === 'ready' && logs.length === 0 && (
                <p>No audit entries match this filter.</p>
            )}

            {state.status === 'ready' && logs.length > 0 && (
                <>
                <div className='table-responsive'>
                <table className='audit-table'>
                    <thead>
                        <tr>
                            <th>Timestamp</th>
                            <th>Action</th>
                            <th>Batch</th>
                            <th>Photo</th>
                            <th>User</th>
                            <th>IP</th>
                        </tr>
                    </thead>
                    <tbody>
                        {logs.map(log => (
                            <tr key={log.id}>
                                <td>{fmtTime(log.timestamp)}</td>
                                <td><code>{log.actionType}</code></td>
                                <td>{fmtBatch(log.batchId, batches)}</td>
                                <td>{fmtPhoto(log.photoId, log.actionType)}</td>
                                <td>{fmtUser(log.userId)}</td>
                                <td>{log.ipAddress || '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
                <div className='audit-pagination'>
                    <button
                        className='audit-page-btn'
                        onClick={() => setPage(p => p - 1)}
                        disabled={page <= 1}
                    >
                        ← Previous
                    </button>
                    <span className='audit-page-info'>
                        {firstEntry}–{lastEntry} of {total} &middot; page {page} of {totalPages}
                    </span>
                    <button
                        className='audit-page-btn'
                        onClick={() => setPage(p => p + 1)}
                        disabled={page >= totalPages}
                    >
                        Next →
                    </button>
                </div>
                </>
            )}
        </div>
    );
}
